/**
 * The drop, on screen (02 §3): the Osprey crossing the map north to south, pods falling
 * under small canopies, the ranger's parafoil with its shadow showing where he is over and
 * how high, landed pods with their beacons, and the canopy he leaves behind.
 */

import { Group, type Material, Mesh, type Scene, type Texture, Vector3 } from 'three';
import type { World } from '../../sim/types';
import type { PixelCamera } from '../camera/PixelCamera';
import { MeshBuilder, place } from '../kit/MeshBuilder';
import { canopy as collapsedCanopy, supplyPod } from '../kit/structures';
import { buildOsprey, type OspreyModel } from '../kit/vehicles';
import { createEmissiveMaterial, createToonMaterial, OUTLINE } from '../materials/toon';
import type { Palette } from '../palette';
import { Pcg32 } from '../../sim/rng';
import { castsFrom, setCastShadow } from './aircraftShadow';
import { flightHeight } from './altitude';

interface PodView {
  root: Group;
  chute: Mesh;
  beacon: Mesh;
}

/** Piping for the 4.4 x 1.7 m ram-air wing at y = 5.2: an edge all round, standing proud of it, and the underside. */
export function parafoilTrim(palette: Palette): MeshBuilder {
  const mb = new MeshBuilder();
  const pale = palette.color('paper.0');
  for (const sz of [-1, 1]) mb.box(4.64, 0.2, 0.14, pale, place(0, 5.3, sz * 0.92));
  for (const sx of [-1, 1]) mb.box(0.14, 0.2, 1.98, pale, place(sx * 2.27, 5.3, 0));
  mb.box(4.3, 0.04, 1.6, pale, place(0, 5.15, 0));
  return mb;
}

export class InsertionView {
  private readonly aircraft: OspreyModel;
  private readonly parafoil: Group;
  private readonly shadow: Mesh;
  private readonly leftBehind: Mesh;
  private readonly pods = new Map<number, PodView>();
  private readonly propMaterial: Material;
  private readonly glowMaterial: Material;
  private readonly flatMaterial: Material;
  private readonly scratch = new Vector3();
  private readonly snapped = new Vector3();

  constructor(
    private readonly scene: Scene,
    private readonly palette: Palette,
    private readonly gradient: Texture,
    private readonly groundY: (x: number, z: number) => number,
  ) {
    this.propMaterial = createToonMaterial({ gradientMap: this.gradient, outline: OUTLINE.prop });
    this.flatMaterial = createToonMaterial({ gradientMap: this.gradient, outline: OUTLINE.none });
    this.glowMaterial = createEmissiveMaterial(this.gradient, 1.2);
    this.aircraft = buildOsprey(palette, this.propMaterial);
    this.aircraft.root.visible = false;
    // The Osprey model's nose points north; the insertion run is flown north to south.
    this.aircraft.root.rotation.y = Math.PI;
    scene.add(this.aircraft.root);

    // Ram-air parafoil: a wide shallow wing in the player's colours, lines implied.
    const wing = new MeshBuilder();
    wing.box(4.4, 0.35, 1.7, { all: palette.color('paper.0'), top: palette.color('playerAccent.0') }, place(0, 5.2, 0));
    for (const x of [-1.8, -0.6, 0.6, 1.8]) wing.box(0.12, 0.4, 1.7, palette.color('canvas.0'), place(x, 4.9, 0));
    this.parafoil = new Group();
    const wingMesh = new Mesh(wing.build(), this.propMaterial);
    wingMesh.castShadow = true;
    // At night the lit wing sinks into the dark ground (M2-M6 report). A self-lit off-white rim
    // round the wing and a pale under-surface hold their colour through the night grade, so the
    // canopy reads as a bright-edged rectangle from any heading; by day they are simply its piping.
    const trimMesh = new Mesh(parafoilTrim(palette).build(), this.glowMaterial);
    trimMesh.name = 'parafoil-trim';
    this.parafoil.add(wingMesh, trimMesh);
    this.parafoil.visible = false;
    scene.add(this.parafoil);

    // A dark disc on the ground under the canopy: where you are over, and (by size) how high.
    const disc = new MeshBuilder();
    disc.cylinder(1, 1, 0.04, 12, palette.color('ink.1'));
    this.shadow = new Mesh(disc.build(), this.flatMaterial);
    this.shadow.visible = false;
    scene.add(this.shadow);

    this.leftBehind = new Mesh(collapsedCanopy(palette, new Pcg32(5, 5)).build(), this.propMaterial);
    this.leftBehind.castShadow = true;
    this.leftBehind.visible = false;
    scene.add(this.leftBehind);
  }

  private podView(id: number): PodView {
    let view = this.pods.get(id);
    if (view) return view;
    const root = new Group();
    const body = new Mesh(supplyPod(this.palette, new Pcg32(id, 3)).build(), createToonMaterial({ gradientMap: this.gradient, outline: OUTLINE.interactive }));
    body.castShadow = true;
    const dome = new MeshBuilder();
    dome.blob(1.5, 0.8, 1.5, this.palette.color('paper.0'), place(0, 3.2, 0), { detail: 1, flatBottom: true });
    const chute = new Mesh(dome.build(), this.propMaterial);
    const lamp = new MeshBuilder();
    lamp.box(0.22, 0.22, 0.22, this.palette.color('playerAccent.0'), place(0, 1.0, 0));
    const beacon = new Mesh(lamp.build(), this.glowMaterial);
    root.add(body, chute, beacon);
    this.scene.add(root);
    view = { root, chute, beacon };
    this.pods.set(id, view);
    return view;
  }

  update(world: World, time: number, dt: number, alpha: number, camera: PixelCamera): void {
    const ins = world.insertion;
    const x = ins.prevX + (ins.x - ins.prevX) * alpha;
    const z = ins.prevZ + (ins.z - ins.prevZ) * alpha;
    const ground = this.groundY(x, z);

    this.aircraft.root.visible = ins.phase === 'flyover';
    // The drop is flown high, and the disc below is the shadow the player is meant to read
    // (aircraftShadow.ts): a real one only snaps in and out at the edge of the sun's frustum.
    setCastShadow(this.aircraft.root, castsFrom(flightHeight(ground, ins.altitude) - ground));
    setCastShadow(this.parafoil, castsFrom(flightHeight(ground, ins.altitude) - ground));
    if (ins.phase === 'flyover') {
      camera.snapToTexel(this.scratch.set(x, flightHeight(ground, ins.altitude), z), this.snapped);
      this.aircraft.root.position.copy(this.snapped);
      this.aircraft.setLadder(false, ins.altitude);
      this.aircraft.update(time, dt);
    }

    const underCanopy = ins.phase === 'descent';
    this.parafoil.visible = underCanopy;
    this.shadow.visible = ins.phase !== 'ground';
    if (ins.phase !== 'ground') {
      camera.snapToTexel(this.scratch.set(x, ground + 0.05, z), this.snapped);
      this.shadow.position.copy(this.snapped);
      // Big and faint-looking when high, tight when about to land.
      const spread = ins.phase === 'flyover' ? 5 : 1.2 + (ins.altitude / 60) * 2.6;
      this.shadow.scale.set(spread, 1, spread * 0.8);
    }
    if (underCanopy) {
      camera.snapToTexel(this.scratch.set(x, flightHeight(ground, ins.altitude), z), this.snapped);
      this.parafoil.position.copy(this.snapped);
      this.parafoil.rotation.y = ins.heading;
    }

    if (ins.canopy && !ins.canopy.hidden) {
      this.leftBehind.visible = true;
      this.leftBehind.position.set(ins.canopy.x, this.groundY(ins.canopy.x, ins.canopy.z), ins.canopy.z);
    } else this.leftBehind.visible = false;

    for (const pod of ins.pods) {
      if (pod.state === 'aboard') continue;
      const view = this.podView(pod.id);
      const shown = pod.state === 'falling' || pod.state === 'landed';
      view.root.visible = shown;
      if (!shown) continue;
      camera.snapToTexel(this.scratch.set(pod.x, flightHeight(this.groundY(pod.x, pod.z), pod.y), pod.z), this.snapped);
      view.root.position.copy(this.snapped);
      view.chute.visible = pod.state === 'falling';
      // The beacon blinks once a second so a landed pod can be found again.
      view.beacon.visible = pod.state === 'landed' && Math.floor(time * 2) % 2 === 0;
    }
  }
}
